import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import SEOHead from '../../components/common/SEOHead';

// Strip old nizus.com domain → relative path; add leading / for bare relative paths
const normalizeImgUrl = (raw) => {
    if (!raw) return null;
    const m = raw.match(/^https?:\/\/(?:www\.)?(?:staging\.)?nizus\.com(\/.*)/i);
    if (m) return m[1];
    if (!raw.startsWith('http') && !raw.startsWith('/')) return '/' + raw;
    return raw;
};

const BlogPost = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { language } = useLanguage();
    const inDashboard = location.pathname.startsWith('/dashboard');
    const blogBase = inDashboard ? '/dashboard/account/blog' : '/blog';

    const [blog, setBlog]           = useState(null);
    const [paragraphs, setParagraphs] = useState([]);
    const [loading, setLoading]     = useState(true);
    const [notFound, setNotFound]   = useState(false);

    useEffect(() => {
        if (!slug) return;
        setLoading(true);
        setNotFound(false);

        api.get('/blog.php', { params: { action: 'get', slug, lang: language } })
            .then(res => {
                setBlog(res.data.blog);
                setParagraphs(res.data.paragraphs || []);
            })
            .catch(err => {
                if (err.response?.status === 404) setNotFound(true);
            })
            .finally(() => setLoading(false));
    }, [slug, language]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500" />
            </div>
        );
    }

    if (notFound || !blog) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-black flex flex-col items-center justify-center gap-4 text-white">
                <p className="text-xl font-bold">{language === 'en' ? 'Article not found' : 'Article introuvable'}</p>
                <button onClick={() => navigate(blogBase)} className="px-5 py-2 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition">
                    {language === 'en' ? 'Back to Blog' : 'Retour au blogue'}
                </button>
            </div>
        );
    }

    const title    = language === 'en' ? (blog.title_en    || blog.title_fr)    : (blog.title_fr    || blog.title_en);
    const category = language === 'en' ? (blog.category_en || blog.category_fr) : (blog.category_fr || blog.category_en);
    const metaTitle = language === 'en' ? blog.metaTitle_en : blog.metaTitle_fr;
    const rawKeywords = language === 'en' ? (blog.keywords_en || blog.keywords_fr) : (blog.keywords_fr || blog.keywords_en);
    const keywords = rawKeywords ? rawKeywords.split(',').map(k => k.trim()).filter(Boolean) : [];
    const metaDesc = language === 'en' ? blog.metaDesc_en : blog.metaDesc_fr;
    const previewImg = language === 'en'
        ? normalizeImgUrl(blog.previewimg_en || blog.previewimg_fr)
        : normalizeImgUrl(blog.previewimg_fr || blog.previewimg_en);

    const articleJsonLd = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": metaTitle || title,
        "description": metaDesc || '',
        "image": previewImg || '/img/og-nizus-default.jpg',
        "publisher": {
            "@type": "Organization",
            "name": "Nizus Online Pilot Training",
        },
        "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `https://www.nizus.com/blog/${slug}`
        }
    };

    // Layout (container/padding/bg) comes from parent: PublicContentLayout or AccountLayout.
    return (
        <>
            <SEOHead
                title={metaTitle || title}
                description={metaDesc}
                keywords={rawKeywords}
                canonical={`/blog/${slug}`}
                alternates={{
                    fr: blog.url_fr ? `/blog/${blog.url_fr}` : null,
                    en: blog.url_en ? `/blog/${blog.url_en}` : null,
                }}
                ogImage={previewImg}
                ogType="article"
                jsonLd={articleJsonLd}
                article={{ section: category }}
            />
            <div className="max-w-3xl mx-auto">
                {/* Back button — public only. In dashboard, AccountLayout's "Mon compte" link handles return. */}
                {!inDashboard && (
                    <button
                        onClick={() => navigate(blogBase)}
                        className="flex items-center gap-2 text-sm text-white/50 hover:text-blue-600 dark:hover:text-blue-400 transition mb-8"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        {language === 'en' ? 'Back to Blog' : 'Retour au blogue'}
                    </button>
                )}

                {/* Header */}
                {category && (
                    <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide">
                        {category}
                    </span>
                )}
                <h1 className="text-3xl md:text-4xl font-bold mt-2 leading-tight">
                    {title}
                </h1>

                {/* Keywords pills */}
                {keywords.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4 mb-10">
                        {keywords.map((kw, i) => (
                            <span
                                key={i}
                                className="px-3 py-1 text-xs font-medium border border-blue-500/40 dark:border-blue-400/30 text-blue-600 dark:text-blue-400 rounded-full"
                            >
                                {kw}
                            </span>
                        ))}
                    </div>
                )}
                {keywords.length === 0 && <div className="mb-10" />}

                {/* Paragraphs */}
                <div className="space-y-10">
                    {paragraphs.map(p => {
                        const content = language === 'en' ? (p.libelle_en || p.libelle_fr) : (p.libelle_fr || p.libelle_en);
                        const imgUrl  = normalizeImgUrl(language === 'en'
                            ? (p.media_en_url || p.media_all_url)
                            : (p.media_fr_url || p.media_all_url));
                        const isCentered = p.displayOption === 'CENTER';

                        return (
                            <div key={p.idBlogParagraph} className="prose prose-gray dark:prose-invert max-w-none">
                                {content && (
                                    <div
                                        dangerouslySetInnerHTML={{ __html: content }}
                                        className="text-gray-700 dark:text-gray-300 leading-relaxed blog-content"
                                    />
                                )}
                                {imgUrl && (
                                    <div className={`mt-4 ${isCentered ? 'text-center' : ''}`}>
                                        <img
                                            src={imgUrl}
                                            alt=""
                                            className={`rounded-xl max-w-full ${isCentered ? 'mx-auto' : ''}`}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Footer nav — public only. In dashboard, AccountLayout's "Mon compte" link handles return. */}
                {!inDashboard && (
                    <div className="mt-16 pt-8 border-t border-white/[0.08]">
                        <button
                            onClick={() => navigate(blogBase)}
                            className="flex items-center gap-2 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                            {language === 'en' ? 'Back to Blog' : 'Retour au blogue'}
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};

export default BlogPost;
